import { memo, useMemo } from 'react';

import { CookingStationName, FCStatus, FoodComponent } from '../../../../libs';
import { getFoodComponentStatusAttributes } from '../../../../libs/utils/helperFunctions';
import { getCombinedShortages } from '../../ComponentsCheckHelpers';
import SectionTableRow from '../SectionTableRow';

const NO_SELECTION = new Set<string>();

interface SectionProps {
  tableName: string;
  foodComponent: FoodComponent;
  highlightedComponentId?: string | null;
  onHighlightEnd?: () => void;
  showAllUnits: boolean;
  selectedHours: string;
  selectedIdsSet?: Set<string>;
  onComponentSelection?: (component: FoodComponent, isSelected: boolean) => void;
  fixFirstTwoColumns?: boolean;
  hasCheckboxColumn?: boolean;
}

const Section = ({
  tableName,
  foodComponent: rawFoodComponent,
  highlightedComponentId,
  onHighlightEnd,
  selectedHours,
  showAllUnits,
  selectedIdsSet = NO_SELECTION,
  onComponentSelection,
  fixFirstTwoColumns = false,
  hasCheckboxColumn = false
}: SectionProps) => {
  const combinedShortages = useMemo(
    () => getCombinedShortages(rawFoodComponent.shortages, tableName),
    [rawFoodComponent.shortages, tableName]
  );
  const foodComponent = useMemo(() => {
    const cookingStations = rawFoodComponent.cookingStations ?? [];
    return cookingStations[cookingStations?.length - 1].name === CookingStationName.portioning
      ? { ...rawFoodComponent, cookingStations: cookingStations.slice(0, cookingStations?.length - 1) }
      : rawFoodComponent;
  }, [rawFoodComponent]);
  const statusAttributes = useMemo(
    () => getFoodComponentStatusAttributes(foodComponent.cookingStations ?? [], tableName),
    [foodComponent, tableName]
  );

  return (
    <>
      <SectionTableRow
        key={foodComponent.id}
        foodComponent={foodComponent}
        isChecked={Boolean(
          foodComponent.cookingStations?.some((station) => station.name === tableName && station.status === FCStatus.completed)
        )}
        statusAttributes={statusAttributes}
        tableName={tableName}
        highlight={highlightedComponentId === foodComponent.id}
        onHighlightEnd={onHighlightEnd}
        showAllUnits={showAllUnits}
        selectedHours={selectedHours}
        selectedIdsSet={selectedIdsSet}
        onComponentSelection={onComponentSelection}
        fixFirstTwoColumns={fixFirstTwoColumns}
        hasCheckboxColumn={hasCheckboxColumn}
      />
      {combinedShortages
        ? Object.entries(combinedShortages).map(
            ([type, shortages]) =>
              shortages.ids.length > 0 && (
                <SectionTableRow
                  key={shortages.ids.join(';')}
                  foodComponent={foodComponent}
                  isChecked={type === 'completed' ? true : false}
                  statusAttributes={getFoodComponentStatusAttributes(shortages.cookingStations ?? [], tableName)}
                  tableName={tableName}
                  combinedShortages={shortages}
                  showAllUnits={showAllUnits}
                  selectedHours={selectedHours}
                  fixFirstTwoColumns={fixFirstTwoColumns}
                  hasCheckboxColumn={hasCheckboxColumn}
                />
              )
          )
        : ''}
    </>
  );
};

export default memo(Section);
